import { CommonModule } from '@angular/common';
import type { ElementRef, OnInit } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    from,
    map,
    of,
    scan,
    startWith,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';

import { SingleImageComponent } from '../../components/single-image/single-image.component';
import { UnsplashProvider } from '../../providers/unsplash';
import { type ImageResult } from '../../types';

const IMAGES_PER_PAGE = 30;

@Component({
    selector: 'sp-image-search',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SingleImageComponent,
        InfiniteScrollModule,
        TranslateModule,
    ],
    templateUrl: './image-search.component.html',
    styleUrls: ['./image-search.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageSearchComponent implements OnInit, OnDestroy {
    @Input() public apiUrl!: string;
    @Output() public readonly selectedImage = new EventEmitter<ImageResult>();

    @ViewChild('scrollTarget') scrollTarget!: ElementRef<HTMLDivElement>;

    private readonly translate = inject(TranslateService);
    private imageProvider!: UnsplashProvider;

    public scrollDistance = 1;
    public query = new FormControl<string>('');

    private readonly page$ = new BehaviorSubject<number>(1);
    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.imageProvider = new UnsplashProvider({ apiUrl: this.apiUrl });
    }

    private queryChanges$ = this.query.valueChanges.pipe(
        startWith(''),
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(300),
        map((q) => q?.trim() || ''),
        tap(() => {
            // Whenever the user changes query, reset to page=1
            this.scrollTarget.nativeElement.scrollIntoView({ behavior: 'smooth' });
            this.page$.next(1);
        })
    );

    public images$ = combineLatest([this.queryChanges$, this.page$]).pipe(
        debounceTime(0),
        takeUntil(this.destroy$),
        switchMap(([query, page]) => {
            const request$ = !query
                ? from(this.imageProvider.list({ page, perPage: IMAGES_PER_PAGE }))
                : from(this.imageProvider.search({ query, page, perPage: IMAGES_PER_PAGE }));

            return request$.pipe(
                catchError((error) => {
                    console.error('Error loading images:', this.getErrorMessage(error));
                    // return [
                    //     NotifyActions.showNotify({
                    //         message: `Error: ${error}`,
                    //         toastType: 'error',
                    //     }),
                    // ];
                    return of(null);
                })
            );
        }),
        scan((images: ImageResult[], newImages) => {
            if (!newImages) {
                return images;
            }
            if (this.page$.value === 1) {
                return newImages;
            } else {
                return [...images, ...newImages];
            }
        }, [])
    );

    public loadMore(): void {
        this.page$.next(this.page$.value + 1);
    }

    public selectImage(image: ImageResult): void {
        this.selectedImage.emit(image);
    }

    private getErrorMessage(error: any): string {
        if (error.status === 429 || error.status === 403) {
            return 'Rate limit exceeded. Please try again later.';
        }

        return this.translate.instant('image_upload_error');
    }

    public onScroll(event: any) {
        this.loadMore();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
